import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { AlertTriangle, ArrowDown, ArrowUp, Info, Plus, Trash2 } from 'lucide-react';
import type {
  PromotionRuleDto,
  PromotionRuleInputDto,
  PromotionRuleTemplateOptionDto,
} from '@kardume/types';
import { ActionButton, Button, Checkbox, TextInput } from '@kardume/ui';

/**
 * Limites do core (`PromotionRuleService`), repetidos aqui porque `@kardume/types`
 * ainda não os exporta. Servem só para a tela barrar antes do request - quem
 * manda continua sendo o backend.
 */
export const PROMOTION_RULE_MAX_COUNT = 12;
export const PROMOTION_RULE_MAX_TEXT_LENGTH = 300;

/** Uma condição do rascunho que aponta para uma cláusula do catálogo. */
export interface PromotionRuleTemplateDraft {
  kind: 'template';
  templateId: string;
  /**
   * Texto que o consumidor lê. Guardado no rascunho (e não só no catálogo)
   * porque uma cláusula desativada depois de salva some do catálogo e este é o
   * único lugar de onde a linha ainda pode ser desenhada.
   */
  text: string;
}

/** Uma condição escrita pela própria loja. */
export interface PromotionRuleCustomDraft {
  kind: 'custom';
  customText: string;
}

/**
 * O rascunho é uma lista de itens que são OU cláusula do catálogo OU texto
 * próprio - nunca os dois, que é o que o core rejeita como ambíguo. A união
 * discriminada torna o estado inválido impossível de representar.
 */
export type PromotionRuleDraft = PromotionRuleTemplateDraft | PromotionRuleCustomDraft;

const isTemplateDraft = (item: PromotionRuleDraft): item is PromotionRuleTemplateDraft =>
  item.kind === 'template';

const isCustomDraft = (item: PromotionRuleDraft): item is PromotionRuleCustomDraft =>
  item.kind === 'custom';

/** Reconstrói o rascunho a partir do que o anúncio salvo devolveu. */
export const promotionRulesToDraft = (rules?: PromotionRuleDto[] | null): PromotionRuleDraft[] =>
  (rules ?? []).map((rule): PromotionRuleDraft =>
    rule.templateId
      ? { kind: 'template', templateId: rule.templateId, text: rule.text }
      : // `customText` é o que o lojista digitou; `text` é o mesmo conteúdo já
        // resolvido pelo core. O fallback cobre um payload antigo sem `customText`.
        { kind: 'custom', customText: rule.customText ?? rule.text }
);

/**
 * Ordena o rascunho exatamente como a tela o mostra: primeiro as cláusulas do
 * catálogo (na ordem que a operação definiu), depois as condições próprias.
 *
 * A ordem importa - é a ordem em que o consumidor lê -, e a única garantia que
 * conseguimos dar é que o que sai no payload é o que estava na tela. Como o
 * bloco de cláusulas é uma lista de caixas em ordem de catálogo, editar um
 * anúncio antigo pode reordenar as condições dele (as do catálogo sobem).
 */
const orderForDisplay = (
  draft: PromotionRuleDraft[],
  templates: PromotionRuleTemplateOptionDto[]
): PromotionRuleDraft[] => {
  const catalogIndex = (templateId: string) => {
    const index = templates.findIndex((option) => option.id === templateId);
    // Cláusula desativada depois de salva não está no catálogo: vai para o fim
    // do bloco, preservando a ordem relativa entre as desativadas.
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return [
    ...draft
      .filter(isTemplateDraft)
      .sort((a, b) => catalogIndex(a.templateId) - catalogIndex(b.templateId)),
    ...draft.filter(isCustomDraft),
  ];
};

/**
 * Monta a lista que vai no `PromotionRequestDto.rules`, na ordem em que a tela
 * mostra. Linhas de texto próprio ainda em branco são slots que o lojista abriu
 * e não preencheu: saem do payload em vez de virar erro (texto vazio o core
 * recusa).
 */
export const promotionRulesToPayload = (
  draft: PromotionRuleDraft[],
  templates: PromotionRuleTemplateOptionDto[]
): PromotionRuleInputDto[] =>
  orderForDisplay(draft, templates)
    .filter((item) => (isCustomDraft(item) ? !!item.customText.trim() : true))
    .map((item) =>
      isTemplateDraft(item) ? { templateId: item.templateId } : { customText: item.customText.trim() }
    );

/** Quantas condições o anúncio realmente terá (ignora as linhas em branco). */
const countEffective = (draft: PromotionRuleDraft[]): number =>
  draft.filter((item) => (isCustomDraft(item) ? !!item.customText.trim() : true)).length;

export interface PromotionRuleErrors {
  /** Mensagem por linha de texto próprio, indexada pela posição no rascunho. */
  rows: Record<number, string>;
  /** Erro do conjunto (hoje só o limite de condições). */
  general?: string;
}

/**
 * Espelha as validações do core para o lojista ver o problema antes do request.
 *
 * Duplicado é comparado por texto aparado e SEM diferenciar maiúsculas, porque
 * é assim que o core compara (`PromotionRuleService.rejectDuplicates` reduz o
 * texto próprio a minúsculas antes de procurar repetição). Comparar com caixa
 * aqui deixaria "Um cupom por pessoa." e "um cupom por pessoa." passarem pela
 * tela para serem recusadas pelo servidor com uma mensagem genérica, sem
 * apontar qual das duas linhas é a repetida.
 */
export const validatePromotionRules = (draft: PromotionRuleDraft[]): PromotionRuleErrors => {
  const rows: Record<number, string> = {};
  const seen = new Set<string>();

  draft.forEach((item, index) => {
    if (!isCustomDraft(item)) return;
    const text = item.customText.trim();
    if (!text) return;

    const key = text.toLowerCase();
    if (text.length > PROMOTION_RULE_MAX_TEXT_LENGTH) {
      rows[index] = `No máximo ${PROMOTION_RULE_MAX_TEXT_LENGTH} caracteres.`;
    } else if (seen.has(key)) {
      rows[index] = 'Esta condição já foi adicionada.';
    }
    seen.add(key);
  });

  const total = countEffective(draft);

  return {
    rows,
    general:
      total > PROMOTION_RULE_MAX_COUNT
        ? `Um anúncio aceita no máximo ${PROMOTION_RULE_MAX_COUNT} condições - remova ${
            total - PROMOTION_RULE_MAX_COUNT
          }.`
        : undefined,
  };
};

/** True enquanto houver algo a corrigir - a tela usa isso para travar o submit. */
export const hasPromotionRuleErrors = (errors: PromotionRuleErrors): boolean =>
  !!errors.general || Object.keys(errors.rows).length > 0;

/** Por que a mudança aconteceu, para a tela saber se o lojista mexeu no campo. */
export interface PromotionRuleChangeMeta {
  /**
   * True quando o lojista de fato alterou o conjunto de condições. Abrir uma
   * linha em branco (ou fechá-la sem digitar) não conta: sem isso, clicar em
   * "Adicionar" por engano num anúncio novo já apagaria as condições padrão.
   */
  touched: boolean;
}

const Card = styled.div`
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SectionTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.15rem;
  cursor: pointer;
  line-height: 1.35;

  /* O Checkbox centraliza a caixa; com cláusula de duas linhas ela sobe para o topo. */
  > span {
    align-items: flex-start;
  }
`;

const InactiveTag = styled.span`
  margin-left: 0.4rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: ${(p) => p.theme.textMuted};
  white-space: nowrap;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 0.6rem;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  /* Alinha os botões com o campo de 42px, que fica acima do texto de erro. */
  min-height: 42px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Muted = styled.span`
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

const Hint = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.4;
  color: ${(p) => p.theme.textMuted};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`;

const ErrorText = styled(Hint)`
  color: ${(p) => p.theme.error};
`;

export interface PromotionRulesInputProps {
  /** Rascunho das condições. A tela é a dona do estado. */
  value: PromotionRuleDraft[];
  onChange: (rules: PromotionRuleDraft[], meta: PromotionRuleChangeMeta) => void;
  /** Catálogo de cláusulas ativas vindo do core. Nunca escrever essa lista no cliente. */
  templates: PromotionRuleTemplateOptionDto[];
  templatesLoading?: boolean;
  templatesError?: string | null;
  /** 'create' mostra o aviso sobre as condições padrão da plataforma. */
  mode: 'create' | 'edit';
  /** Se o lojista já mexeu no campo (só usado para o aviso do modo 'create'). */
  touched?: boolean;
  disabled?: boolean;
}

/**
 * Editor das condições que o anúncio exibe ao consumidor.
 *
 * Duas origens: as cláusulas do catálogo que a operação mantém (caixas de
 * marcação) e os textos próprios da loja (linhas de texto). Cada condição é uma
 * coisa OU a outra - o core recusa um item com `templateId` e `customText`
 * juntos -, e é por isso que o rascunho é uma união discriminada em vez de um
 * objeto com os dois campos opcionais.
 */
export const PromotionRulesInput: React.FC<PromotionRulesInputProps> = ({
  value,
  onChange,
  templates,
  templatesLoading,
  templatesError,
  mode,
  touched,
  disabled,
}) => {
  const theme = useTheme();
  const errors = useMemo(() => validatePromotionRules(value), [value]);

  const checkedIds = useMemo(
    () => new Set(value.filter(isTemplateDraft).map((item) => item.templateId)),
    [value]
  );

  /**
   * Cláusulas que o anúncio usa e que não estão mais no catálogo (a operação as
   * desativou depois). Precisam continuar visíveis e marcadas: se sumissem,
   * salvar a edição apagaria em silêncio uma condição que o anúncio exibe hoje.
   * Ficam desmarcáveis - tirar é decisão do lojista, não efeito colateral.
   */
  const orphanTemplates = useMemo(
    () =>
      value
        .filter(isTemplateDraft)
        .filter((item) => !templates.some((option) => option.id === item.templateId)),
    [value, templates]
  );

  /** Linhas de texto próprio junto com a posição delas no rascunho. */
  const customRows = useMemo(
    () =>
      value
        .map((item, index) => ({ item, index }))
        .filter((entry): entry is { item: PromotionRuleCustomDraft; index: number } =>
          isCustomDraft(entry.item)
        ),
    [value]
  );

  const total = countEffective(value);
  const atMax = total >= PROMOTION_RULE_MAX_COUNT;

  const toggleTemplate = (option: { id: string; text: string }, checked: boolean) => {
    const next = checked
      ? [...value, { kind: 'template' as const, templateId: option.id, text: option.text }]
      : value.filter((item) => !(isTemplateDraft(item) && item.templateId === option.id));
    onChange(next, { touched: true });
  };

  const patchCustom = (index: number, customText: string) =>
    onChange(
      value.map((item, i) => (i === index ? { kind: 'custom' as const, customText } : item)),
      { touched: true }
    );

  const removeAt = (index: number) => {
    const removed = value[index];
    onChange(value.filter((_, i) => i !== index), {
      touched: isTemplateDraft(removed) || !!removed.customText.trim(),
    });
  };

  /** Troca duas linhas de texto próprio de lugar - a ordem é a ordem de leitura. */
  const moveCustom = (position: number, offset: number) => {
    const from = customRows[position].index;
    const to = customRows[position + offset].index;
    const next = [...value];
    next[from] = value[to];
    next[to] = value[from];
    onChange(next, { touched: true });
  };

  const addCustom = () =>
    // Abrir a linha ainda não é mexer nas condições; só digitar nela é.
    onChange([...value, { kind: 'custom', customText: '' }], { touched: false });

  const hasCatalog = templates.length > 0 || orphanTemplates.length > 0;

  return (
    <Card>
      <Section>
        <SectionTitle>Condições da plataforma</SectionTitle>

        {templatesLoading && <Muted>Carregando cláusulas...</Muted>}
        {!templatesLoading && templatesError && (
          <ErrorText>
            <AlertTriangle size={14} />
            <span>{templatesError}</span>
          </ErrorText>
        )}
        {!templatesLoading && !templatesError && !hasCatalog && (
          <Muted>A plataforma ainda não cadastrou cláusulas.</Muted>
        )}

        {templates.map((option) => {
          const checked = checkedIds.has(option.id);
          return (
            <CheckRow key={option.id}>
              <Checkbox
                checked={checked}
                // No limite, só dá para desmarcar - marcar mais uma seria um request recusado.
                disabled={disabled || (!checked && atMax)}
                onChange={(e) => toggleTemplate(option, e.target.checked)}
                label={option.text}
              />
            </CheckRow>
          );
        })}

        {orphanTemplates.map((item) => (
          <CheckRow key={item.templateId}>
            <Checkbox
              checked
              disabled={disabled}
              onChange={() => toggleTemplate({ id: item.templateId, text: item.text }, false)}
              label={
                <>
                  {item.text}
                  <InactiveTag>· descontinuada</InactiveTag>
                </>
              }
            />
          </CheckRow>
        ))}
      </Section>

      <Section>
        <SectionTitle>Condições próprias da loja</SectionTitle>

        {customRows.length === 0 && <Muted>Nenhuma condição própria.</Muted>}

        {customRows.map(({ item, index }, position) => (
          <Row key={index}>
            <TextInput
              aria-label={`Condição própria ${position + 1}`}
              placeholder="Ex.: Válido apenas para consumo no local"
              value={item.customText}
              maxLength={PROMOTION_RULE_MAX_TEXT_LENGTH}
              error={errors.rows[index]}
              disabled={disabled}
              onChange={(e) => patchCustom(index, e.target.value)}
            />
            <RowActions>
              <ActionButton
                type="button"
                title="Subir"
                disabled={disabled || position === 0}
                onClick={() => moveCustom(position, -1)}
              >
                <ArrowUp size={15} />
              </ActionButton>
              <ActionButton
                type="button"
                title="Descer"
                disabled={disabled || position === customRows.length - 1}
                onClick={() => moveCustom(position, 1)}
              >
                <ArrowDown size={15} />
              </ActionButton>
              <ActionButton
                type="button"
                title="Remover"
                color={theme.error}
                disabled={disabled}
                onClick={() => removeAt(index)}
              >
                <Trash2 size={15} />
              </ActionButton>
            </RowActions>
          </Row>
        ))}
      </Section>

      {errors.general && (
        <ErrorText>
          <AlertTriangle size={14} />
          <span>{errors.general}</span>
        </ErrorText>
      )}

      {/*
        Na criação o formulário não sabe quais cláusulas são padrão: o endpoint
        do lojista devolve só `id` e `text`. Em vez de adivinhar, a lista abre
        toda desmarcada e o aviso conta o que vai acontecer em cada caso - sem
        isso o lojista não teria como distinguir "deixei como está" de "quero um
        anúncio sem condição nenhuma".
      */}
      {mode === 'create' && total === 0 && (
        <Hint>
          <Info size={14} />
          <span>
            {touched
              ? 'Este anúncio será criado sem nenhuma condição.'
              : 'Se você não marcar nenhuma condição, o anúncio será criado com as condições padrão da plataforma.'}
          </span>
        </Hint>
      )}

      <Footer>
        <Button type="button" variant="outline" disabled={disabled || atMax} onClick={addCustom}>
          <Plus size={16} /> Adicionar condição
        </Button>
        <Muted>
          {total}/{PROMOTION_RULE_MAX_COUNT} condições
        </Muted>
      </Footer>
    </Card>
  );
};

export default PromotionRulesInput;
